/**
 * ProfileStep Component
 * Collects basic profile information during setup
 */

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, ArrowRight, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SetupProfileData } from '../types';

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(255),
  lastName: z.string().min(1, 'Last name is required').max(255),
  companyName: z.string().max(255).optional(),
  jobTitle: z.string().max(255).optional(),
  timezone: z.string().default('Europe/Zurich'),
  language: z.enum(['de', 'fr', 'it', 'en']).default('de'),
});

type ProfileFormData = z.infer<typeof profileSchema>;

// Common timezones for Swiss users
const TIMEZONES = [
  { value: 'Europe/Zurich', label: 'Switzerland (Zurich)' },
  { value: 'Europe/Berlin', label: 'Germany (Berlin)' },
  { value: 'Europe/Paris', label: 'France (Paris)' },
  { value: 'Europe/Rome', label: 'Italy (Rome)' },
  { value: 'Europe/Vienna', label: 'Austria (Vienna)' },
  { value: 'Europe/London', label: 'United Kingdom (London)' },
  { value: 'America/New_York', label: 'US Eastern (New York)' },
  { value: 'America/Los_Angeles', label: 'US Pacific (Los Angeles)' },
  { value: 'UTC', label: 'UTC' },
];

// Swiss national languages
const LANGUAGES = [
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'it', label: 'Italiano' },
  { value: 'en', label: 'English' },
];

interface ProfileStepProps {
  data: Partial<SetupProfileData>;
  onChange: (data: Partial<SetupProfileData>) => void;
  onContinue: () => void;
  onBack: () => void;
}

export const ProfileStep: React.FC<ProfileStepProps> = ({
  data,
  onChange,
  onContinue,
  onBack,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema) as never,
    defaultValues: {
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      companyName: data.companyName || '',
      jobTitle: data.jobTitle || '',
      timezone: data.timezone || 'Europe/Zurich',
      language: data.language || 'de',
    },
    mode: 'onChange',
  });

  const onSubmit = (formData: ProfileFormData) => {
    onChange(formData);
    onContinue();
  };

  return (
    <div>
      {/* Header icon */}
      <div className="flex justify-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-terracotta-100 dark:bg-terracotta-900/30 flex items-center justify-center">
          <User className="h-8 w-8 text-terracotta-600 dark:text-terracotta-400" />
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit as never)} className="space-y-5">
        {/* Name row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-espresso-700 dark:text-espresso-200 mb-1.5">
              First Name *
            </label>
            <input
              {...register('firstName')}
              className="w-full px-4 py-2.5 rounded-lg border border-cream-300 dark:border-espresso-600 bg-white dark:bg-espresso-800 text-espresso-900 dark:text-cream-100 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200 dark:focus:ring-terracotta-800 transition-colors"
              placeholder="John"
            />
            {errors.firstName && (
              <p className="mt-1 text-xs text-error-600 dark:text-error-400">
                {errors.firstName.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-espresso-700 dark:text-espresso-200 mb-1.5">
              Last Name *
            </label>
            <input
              {...register('lastName')}
              className="w-full px-4 py-2.5 rounded-lg border border-cream-300 dark:border-espresso-600 bg-white dark:bg-espresso-800 text-espresso-900 dark:text-cream-100 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200 dark:focus:ring-terracotta-800 transition-colors"
              placeholder="Doe"
            />
            {errors.lastName && (
              <p className="mt-1 text-xs text-error-600 dark:text-error-400">
                {errors.lastName.message}
              </p>
            )}
          </div>
        </div>

        {/* Company & Job Title */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-espresso-700 dark:text-espresso-200 mb-1.5">
              Company Name
            </label>
            <input
              {...register('companyName')}
              className="w-full px-4 py-2.5 rounded-lg border border-cream-300 dark:border-espresso-600 bg-white dark:bg-espresso-800 text-espresso-900 dark:text-cream-100 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200 dark:focus:ring-terracotta-800 transition-colors"
              placeholder="Acme AG"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-espresso-700 dark:text-espresso-200 mb-1.5">
              Job Title
            </label>
            <input
              {...register('jobTitle')}
              className="w-full px-4 py-2.5 rounded-lg border border-cream-300 dark:border-espresso-600 bg-white dark:bg-espresso-800 text-espresso-900 dark:text-cream-100 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200 dark:focus:ring-terracotta-800 transition-colors"
              placeholder="Legal Counsel"
            />
          </div>
        </div>

        {/* Timezone & Language */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-espresso-700 dark:text-espresso-200 mb-1.5">
              Timezone
            </label>
            <select
              {...register('timezone')}
              className="w-full px-4 py-2.5 rounded-lg border border-cream-300 dark:border-espresso-600 bg-white dark:bg-espresso-800 text-espresso-900 dark:text-cream-100 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200 dark:focus:ring-terracotta-800 transition-colors"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-espresso-700 dark:text-espresso-200 mb-1.5">
              Language
            </label>
            <select
              {...register('language')}
              className="w-full px-4 py-2.5 rounded-lg border border-cream-300 dark:border-espresso-600 bg-white dark:bg-espresso-800 text-espresso-900 dark:text-cream-100 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200 dark:focus:ring-terracotta-800 transition-colors"
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Helper text */}
        <p className="text-xs text-espresso-400 dark:text-espresso-300 text-center">
          You can always update your profile later in Settings.
        </p>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between pt-4">
          <Button
            type="button"
            variant="ghost"
            onClick={onBack}
            className="text-espresso-600 dark:text-espresso-300"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <Button
            type="submit"
            className="bg-terracotta-600 hover:bg-terracotta-700 text-white"
          >
            Continue
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProfileStep;
